import { FC, useState, useEffect } from "react";
import { Card, Row, Col, Select, Space, Spin, Table } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  fetchNewVsReturningData,
  fetchActivationRateOverTimeData,
  fetchFirstTouchData,
  fetchSegmentLiftData,
} from "../actions/acquisitionActions";
import { NewVsReturningData, ActivationRateOverTimeData, FirstTouchData, SegmentLiftData } from "../types/acquisition";
import { COLOR_MAPPING, COLORS } from "../utils/color";
import { useFilters } from "../hooks/useFilters";
import AnalyticsFilters from "../components/AnalyticsFilters";

const { Option } = Select;

const Acquisition: FC = () => {
  const [loading, setLoading] = useState(true);
  const { filters, filterOptions, handleDateRangeChange, handleFilterChange } = useFilters({
    segment_by: "country",
  });

  const [newVsReturningData, setNewVsReturningData] = useState<NewVsReturningData | null>(null);
  const [activationRateData, setActivationRateData] = useState<ActivationRateOverTimeData | null>(null);
  const [firstTouchData, setFirstTouchData] = useState<FirstTouchData | null>(null);
  const [segmentLiftData, setSegmentLiftData] = useState<SegmentLiftData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [newVsReturning, activationRate, firstTouch, segmentLift] = await Promise.all([
          fetchNewVsReturningData(filters),
          fetchActivationRateOverTimeData(filters),
          fetchFirstTouchData(filters),
          fetchSegmentLiftData(filters),
        ]);

        setNewVsReturningData(newVsReturning);
        setActivationRateData(activationRate);
        setFirstTouchData(firstTouch);
        setSegmentLiftData(segmentLift);
      } catch (error) {
        console.error("Error fetching acquisition data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const stackedAreaData =
    newVsReturningData?.data.map((item) => ({
      ...item,
      total: item.new_users + item.returning_users,
    })) || [];

  const activationLineData =
    activationRateData?.data
      .reduce((acc: any[], item) => {
        const existingDate = acc.find((d) => d.date === item.date);
        if (existingDate) {
          existingDate.total_home += item.home_users;
          existingDate.total_signup += item.signup_users;
        } else {
          acc.push({
            date: item.date,
            total_home: item.home_users,
            total_signup: item.signup_users,
          });
        }
        return acc;
      }, [])
      .map((item) => ({
        ...item,
        activation_rate: item.total_home > 0 ? (item.total_signup / item.total_home) * 100 : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date)) || [];

  const segmentedActivationData =
    activationRateData?.data
      .reduce((acc: any[], item) => {
        const existingSegment = acc.find((s) => s.segment === item.segment);
        if (existingSegment) {
          existingSegment.home_users += item.home_users;
          existingSegment.signup_users += item.signup_users;
        } else {
          acc.push({
            segment: item.segment,
            home_users: item.home_users,
            signup_users: item.signup_users,
          });
        }
        return acc;
      }, [])
      .map((item) => ({
        ...item,
        activation_rate: item.home_users > 0 ? (item.signup_users / item.home_users) * 100 : 0,
      })) || [];

  const segmentLiftColumns = [
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
    },
    {
      title: "Home Users",
      dataIndex: "home_users",
      key: "home_users",
    },
    {
      title: "Signup Users",
      dataIndex: "signup_users",
      key: "signup_users",
    },
    {
      title: "Activation Rate (%)",
      dataIndex: "activation_rate",
      key: "activation_rate",
      render: (value: number) => `${value}%`,
    },
    {
      title: "Lift (%)",
      dataIndex: "lift",
      key: "lift",
      render: (value: number) => (
        <span
          style={{
            color: value >= 0 ? COLOR_MAPPING.green : COLOR_MAPPING.red,
          }}
        >
          {value >= 0 ? "+" : ""}
          {value}%
        </span>
      ),
    },
  ];

  if (loading && !newVsReturningData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <AnalyticsFilters
        filters={filters}
        filterOptions={filterOptions}
        onDateRangeChange={handleDateRangeChange}
        onFilterChange={handleFilterChange}
        loading={loading}
        showEmailDomain={false}
      />

      <Card style={{ marginBottom: "24px" }}>
        <Space wrap size="middle">
          <Select
            placeholder="Segment By"
            style={{ width: 150 }}
            value={filters.segment_by}
            onChange={(value) => handleFilterChange("segment_by", value)}
          >
            <Option value="country">Country</Option>
            <Option value="browser">Browser</Option>
            <Option value="email_domain">Email Domain</Option>
          </Select>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="New vs Returning Users" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={stackedAreaData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="new_users"
                  stackId="1"
                  stroke={COLOR_MAPPING.blue}
                  fill={COLOR_MAPPING.blue}
                  name="New Users"
                />
                <Area
                  type="monotone"
                  dataKey="returning_users"
                  stackId="1"
                  stroke={COLOR_MAPPING.green}
                  fill={COLOR_MAPPING.green}
                  name="Returning Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="First Touch Breakdown" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={firstTouchData?.data || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ event, percentage }) => `${event}: ${percentage}%`}
                  outerRadius={80}
                  fill={COLOR_MAPPING.blue}
                  dataKey="percentage"
                >
                  {(firstTouchData?.data || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: string) => [value + "%", "Percentage"]} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Activation Rate Over Time" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={activationLineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(value: string, name: string, props: any) => [
                    `${Number(value).toFixed(2)}% (${props.payload.total_signup}/${props.payload.total_home})`,
                    "Activation Rate",
                  ]}
                />
                <Line type="monotone" dataKey="activation_rate" stroke={COLOR_MAPPING.orange} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={`Activation Rate by ${filters.segment_by}`} loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={segmentedActivationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="segment" />
                <YAxis />
                <Tooltip formatter={(value: string) => [Number(value).toFixed(2) + "%", "Activation Rate"]} />
                <Bar dataKey="activation_rate" fill={COLOR_MAPPING.orange} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={`Segment Lift Analysis - ${filters.segment_by} (Overall: ${segmentLiftData?.overall_activation_rate}%)`}
            loading={loading}
          >
            <Table
              dataSource={segmentLiftData?.data || []}
              columns={segmentLiftColumns}
              rowKey="segment"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Acquisition;
